import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { catchError, forkJoin, Observable, of } from 'rxjs';

import {
    CHART_IMAGE_FEATURE,
    DATA_EXPORT_FEATURE,
} from 'EntitlementsApp/ts/features/feature-needed/feature-needed.constants';
import { ExportOption } from './enum';

import { YChartsSentryUtils } from 'Angular/services/sentry/sentry-utils';
import { FeatureService } from 'EntitlementsApp/ts/features/feature-needed/feature.service';

import { FeatureNeededComponent } from 'EntitlementsApp/ts/features/feature-needed/feature-needed.component';
import { downloadFileFromPostResponse } from 'Angular/utils/download';

import ChartImageHelper from 'ChartApp/ts/features/fundamental-chart/chart-image-helper';

/**
 * Service that supports export related functionality:
 *  - Download image
 *  - Print image with disclosures
 *  - Export chart data to csv
 */
export class ExportService {
    /**
     * @param chartService - Ajax service for fetching chart images and urls. Assumed to have certain methods.
     * @param featureService {FeatureService}
     * @param featureNeededComponent {FeatureNeededComponent}
     * @param exportChartErrorHandler {Function} - callback that runs if the chart image export request fails.
     */
    constructor(
        private chartService: Record<string, any> & {
            getChartSaveAsImageUrl?: (chartServiceParams: any) => Observable<HttpResponse<{ image_url: string }>>;
            getChartCSVDataFromState?: (chartServiceParams: any) => Observable<HttpResponse<Blob>>;
            getChartDisclosure?: (chartServiceParams: any) => Observable<HttpResponse<string>>;
        },
        private featureService: FeatureService,
        private featureNeededComponent: FeatureNeededComponent,
        private exportChartErrorHandler: (response: HttpErrorResponse) => void = (response: HttpErrorResponse) => {}
    ) {}

    /**
     * Create a file including the chart image with disclosures about included metrics and securities.
     * The browser will automatically open the file when the file is successfully created and fetched.
     *
     * @param chartServiceParams {any} - Params passed to chart service methods.
     * @param AIEventsHTML {string | null} - HTML string including AI events summary to be printed below the chart.
     */
    printChart(chartServiceParams: any, AIEventsHTML: string | null = null): void {
        if (!(this.chartService.getChartSaveAsImageUrl && this.chartService.getChartDisclosure)) {
            throw Error('ExportDropdownError:: service is missing getChartSaveAsImageUrl or getChartDisclosure');
        }

        let imageUrl: string;
        let disclosureHTML: string | null;

        const imageRequest$ = this.chartService.getChartSaveAsImageUrl(chartServiceParams).pipe(
            catchError((err) => {
                this.exportChartErrorHandler(err);
                YChartsSentryUtils.captureException(err);
                return of({ ok: false, body: null });
            })
        );
        const disclosureRequest$ = this.chartService.getChartDisclosure(chartServiceParams).pipe(
            catchError((err) => {
                const EXPECTED_ERROR_CODES = [403, 404];
                if (!EXPECTED_ERROR_CODES.includes(err.status)) {
                    YChartsSentryUtils.captureException(err);
                    return of({ ok: false, body: null });
                }
                // a 40x response from the disclosures endpoint just means the user has
                // opted not to include disclosures - we still want to print the chart
                return of({ ok: true, body: '' });
            })
        );

        forkJoin([imageRequest$, disclosureRequest$]).subscribe(([imageRequestResponse, disclosureRequestResponse]) => {
            if (imageRequestResponse.ok && imageRequestResponse.body) {
                imageUrl = imageRequestResponse.body.image_url;
            }
            if (disclosureRequestResponse.ok) {
                disclosureHTML = disclosureRequestResponse.body;
            }

            // Print the chart with the disclosure HTML.
            if (imageUrl) {
                new ChartImageHelper(imageUrl).print(disclosureHTML, AIEventsHTML);
            }
        });
    }

    /**
     * Handle the functionality selected by the user from the "Export" dropdown.
     *
     * @param selectedItem {String} - The name of the option selected from the "Export" dropdown.
     * @param chartServiceParams {any} - Params passed to chart service methods.
     * @param AIEventsHTML {string | null} - HTML string including AI events summary to be printed below the chart.
     */
    async exportHandler(
        selectedItem: string,
        chartServiceParams: any,
        AIEventsHTML: string | null = null
    ): Promise<void> {
        switch (selectedItem) {
            case ExportOption.download:
                if (!this.featureService.userHasFeatures([CHART_IMAGE_FEATURE])) {
                    this.featureNeededComponent.openModal(CHART_IMAGE_FEATURE);
                    break;
                }
                if (!this.chartService.getChartSaveAsImageUrl) {
                    throw Error('ExportDropdownError:: service is missing getChartSaveAsImageUrl');
                }

                const imgResponse: HttpResponse<{ image_url: string }> | any = await this.chartService
                    .getChartSaveAsImageUrl(chartServiceParams)
                    .toPromise()
                    .catch((err: HttpErrorResponse) => {
                        this.exportChartErrorHandler(err);
                        if (err.url && err.status !== 403) {
                            window.open(err.url, '_blank');
                        } else {
                            YChartsSentryUtils.captureException(err);
                        }
                    });
                if (imgResponse && imgResponse.ok && imgResponse.body) {
                    new ChartImageHelper(imgResponse.body.image_url).download();
                }
                break;
            case ExportOption.print:
                if (!this.featureService.userHasFeatures([CHART_IMAGE_FEATURE])) {
                    this.featureNeededComponent.openModal(CHART_IMAGE_FEATURE);
                    break;
                }
                this.printChart(chartServiceParams, AIEventsHTML);
                break;
            case ExportOption.export:
                if (!this.featureService.userHasFeatures([DATA_EXPORT_FEATURE])) {
                    this.featureNeededComponent.openModal(DATA_EXPORT_FEATURE);
                    break;
                }
                if (!this.chartService.getChartCSVDataFromState) {
                    throw Error('ExportDropdownError:: service is missing getChartCSVDataFromState');
                }

                this.chartService.getChartCSVDataFromState(chartServiceParams).subscribe({
                    next: downloadFileFromPostResponse,
                    error: (err: HttpErrorResponse) => {
                        const isBadRequest = err.message.toLowerCase().includes('bad request');
                        this.exportChartErrorHandler(err);
                        if (!isBadRequest) {
                            throw err;
                        }
                    },
                });
                break;
            default:
                break;
        }
    }
}
